/**
 * The bedrock form field — one definition for every text input, textarea and
 * select the marketing site renders.
 *
 * It exists because the same six-utility string was pasted at six call sites
 * across two forms, and it carried two defects that a copy-paste spreads rather
 * than isolates:
 *
 * - `rounded` (4px) against the design's 10–12px, so the contact and DSAR forms
 *   looked like a different product from the search field beside them;
 * - `focus-visible:border-ring`, which emits NOTHING — `ring` is not a
 *   border-colour in the Syncanix preset, so the focused border never changed
 *   and the intended two-part focus treatment (ring + border) was half-missing
 *   on every field on the site.
 *
 * The focus treatment is deliberately two-part: a solid border in brand blue
 * plus a soft 25%-alpha ring. A ring alone reads as a halo floating off the
 * control; the border is what says "this field, right here" — which matters most
 * for the visitor who is tabbing rather than clicking.
 */

export interface FieldStyleProps {
  /** `true` when the field is showing a validation error. */
  readonly invalid?: boolean | undefined;
  readonly className?: string | undefined;
}

export function fieldClasses({ invalid = false, className }: FieldStyleProps = {}): string {
  return [
    'w-full rounded-[11px] border bg-[color:var(--bd-page)] px-3.5 py-2.5 text-base',
    'text-[color:var(--bd-ink)] placeholder:text-[color:var(--bd-faint)]',
    'transition-colors duration-200',
    invalid
      ? // `danger-foreground`, not `destructive`: the latter is a FILL and only
        // 4.48:1 as ink on the light page (`ui-design.md` § 4).
        'border-[color:var(--bd-danger-ink)] focus-visible:ring-[color:color-mix(in_srgb,var(--bd-danger-ink)_25%,transparent)]'
      : 'border-[color:var(--bd-line-2)] hover:border-[color:var(--bd-line-3)] focus-visible:border-[color:var(--bd-blue)] focus-visible:ring-[color:color-mix(in_srgb,var(--bd-blue)_25%,transparent)]',
    'focus-visible:outline-none focus-visible:ring-2',
    'disabled:cursor-not-allowed disabled:opacity-60',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}
